import {
  Box,
  CircularProgress,
  SelectChangeEvent,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { TabInfo } from '../../common/utils/interfaces';
import { IResourceAllocationData, ISkills } from '../type';
import TabsBar from '../../common/widgets/tabs-bar';
import ResourceAllocationFilter from './resource-allocation-filter';
import NoRecords from '../../common/application/no-records';
import ResourceAllocationGrid from './resource-allocation-grid';
import { useQuery } from '@apollo/client';
import { GET_ALL_RESOURCE_ALLOCATIONS, GET_SKILLS } from '../gql';

const tabs: TabInfo[] = [
  { label: 'SE', value: 'Software Engineer' },
  { label: 'QA', value: 'Quality Assurance' },
  { label: 'BA', value: 'Business Analyst' },
  { label: 'PM', value: 'Project Manager' },
  { label: 'DevOps', value: 'DevOps' }
];

const ResourceAllocations = (): JSX.Element => {
  const [tab, setTab] = useState('Software Engineer');
  const [technologyList, setTechnologyList] = useState<ISkills[]>([]);
  const [technologyListValues, setTechnologyListValues] = useState<ISkills[]>(
    []
  );
  const [resourceAllocations, setResourceAllocations] = useState<
    IResourceAllocationData[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [displayName, setDisplayName] = useState<string>('');
  const [numProjects, setNumProjects] = useState<number>();
  const [startValue, setStartValue] = useState<number>(0);
  const [endValue, setEndValue] = useState<number>(100);
  const [skillsFilter, setSkillsFilter] = useState<ISkills[]>([]);

  const { refetch, data: resourceData } = useQuery(
    GET_ALL_RESOURCE_ALLOCATIONS,
    {
      variables: {
        displayName,
        jobTitle: tabs.find((tabInfo) => tabInfo.value === tab)?.label,
        numProjects,
        totalManHoursPerWeek: {
          start: startValue,
          end: endValue
        },
        skillsFilter: skillsFilter.map((skill) => ({
          skillId: skill.id,
          proficiencyLevel: skill.proficiencyLevel
        })),
        pagination: {
          skip: 0,
          take: 23
        }
      }
    }
  );

  const { data: skillsData } = useQuery(GET_SKILLS);

  useEffect(() => {
    if (resourceData) {
      setResourceAllocations(resourceData.getAllResourceAllocations);
      setIsLoading(false);
    }
  }, [resourceData]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      setIsLoading(true);
      await refetch();
      setIsLoading(false);
    };

    void fetchData();
  }, [displayName, numProjects, startValue, endValue, skillsFilter]);

  useEffect(() => {
    if (skillsData) {
      const mappedList = skillsData.getSkills.flatMap(
        (obj: { id: string; name: string }) => [
          {
            ...obj,
            proficiencyLevel: 'EXPERT',
            displayName: obj.name + ' - ' + 'Expert'
          },
          {
            ...obj,
            proficiencyLevel: 'INTERMEDIATE',
            displayName: obj.name + ' - ' + 'Intermediate'
          }
        ]
      );
      setTechnologyList(mappedList);
    }
  }, [skillsData]);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newTab: string
  ): void => {
    setResourceAllocations([]);
    setTab(newTab);
    setIsLoading((state) => !state);
    setResourceAllocations([]);
  };

  const handleClose = (): void => {
    const selectedOptions = technologyListValues
      .map((displayNames) => {
        const option = technologyList.find(
          (options: ISkills) =>
            options.displayName === (displayNames as unknown as string)
        );
        if (option) {
          return {
            id: option.id,
            proficiencyLevel: option.proficiencyLevel
          };
        }
        return null;
      })
      .filter((option) => option !== null);
    setSkillsFilter(selectedOptions as ISkills[]);
  };

  const handleTechnologyStatusChange = (
    event: SelectChangeEvent<ISkills[]>
  ): void => {
    const updatedValue = event.target.value as ISkills[];

    setTechnologyListValues(updatedValue);
  };

  const handleScroll = (event: React.UIEvent<HTMLElement>): void => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 1) {
      const currentLength = resourceAllocations.length;
      void handleLoadMore(currentLength);
    }
  };

  const handleLoadMore = async (skipCount: number): Promise<void> => {
    await refetch({
      pagination: {
        skip: 0,
        take: 23 + skipCount
      }
    });
  };

  return (
    <Box
      data-testid={'resource-allocation-container'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        allignItems: 'flex-start',
        padding: '21px 20px',
        height: 'calc(100vh - 75px)',
        margin: '0px 22px 20px 22px',
        borderRadius: '12px',
        gap: '10px',
        backgroundColor: '#FFFFFF'
      }}
    >
      <Box
        sx={{
          '@media (max-height: 520px)': {
            overflowY: 'auto'
          },
          height: '100%',
          overflowY: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          scrollPadding: '0px',
          '&::-webkit-scrollbar': {
            width: '7px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(217, 217, 217, 1)',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(217, 217, 217, 0.4)',
            borderRadius: '10px'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '11px',
            color: '#46464F',
            alignItems: 'flex-start'
          }}
        >
          <Typography sx={{ fontSize: '28px', fontWeight: 700 }}>
            {`Resource Allocation`}
          </Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
            {'User Management > Resource Allocation'}
          </Typography>
        </Box>
        <Box>
          <Box
            sx={{
              display: 'flex',
              marginTop: '20px',
              flexDirection: 'column',
              marginBottom: '10px'
            }}
          >
            <TabsBar tabs={tabs} tab={tab} handleTabChange={handleTabChange} />
            <Box
              sx={{
                height: '1px',
                background: '#E0E2EC'
              }}
            />
          </Box>

          <Box sx={{ marginBottom: '10px' }}>
            <ResourceAllocationFilter
              onNameChangeCallback={(value) => setDisplayName(value)}
              onNoOfProjectsChangeCallback={(value) => setNumProjects(value)}
              onCommittedCallback={(start, end) => {
                setStartValue(start);
                setEndValue(end);
              }}
              values={technologyListValues}
              options={technologyList}
              label={'Tools/Technology/Competency'}
              handleStatusChange={handleTechnologyStatusChange}
              handleClose={handleClose}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems:
              resourceAllocations.length === 0 ? 'center' : 'flex-start',
            overflowY: resourceAllocations.length === 0 ? 'none' : 'scroll',
            minHeight: '200px',
            scrollPadding: '0px',
            '&::-webkit-scrollbar': {
              width: '7px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(217, 217, 217, 1)',
              borderRadius: '10px'
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(217, 217, 217, 0.4)',
              borderRadius: '10px'
            }
          }}
          onScroll={handleScroll}
        >
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pointerEvents: 'none'
              }}
            >
              <CircularProgress />
            </Box>
          ) : resourceAllocations.length !== 0 ? (
            <ResourceAllocationGrid resourceAllocation={resourceAllocations} />
          ) : (
            <NoRecords message={'No Resource Allocations Available'} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ResourceAllocations;
